/**
 * The trading-action catalogue (SPEC §9.7, §9.10). Order entry is live only on the local mock and
 * Rithmic Test (owner 2026-09-27, ADR-0021; SPEC-ADDENDA A2 amendment); the gate itself lives in
 * `@helm/core` trading/gate.ts. Controls that are still not wired (and the palette entries, which
 * have no symbol or account context) stay disabled with this reason. Nothing in this module sends
 * anything.
 */

export const TRADING_DISABLED_REASON =
  "Not wired yet (A2): trade from a DOM ladder or order pad, or with its hotkeys while it has focus";

/** Trading actions with their SPEC §9.10 default hotkeys and §9.7 semantics for tooltips. */
export type TradingActionId =
  | "trade.buy_market"
  | "trade.sell_market"
  | "trade.join_bid"
  | "trade.join_ask"
  | "trade.reverse"
  | "trade.exit_cancel"
  | "trade.cancel_all"
  | "trade.flatten"
  | "trade.flatten_all";

export type TradingAction = {
  id: TradingActionId;
  /** Palette title (SPEC §9.7 explicit names). */
  title: string;
  /** Display hotkey. */
  hotkey: string;
  /** `KeyboardEvent.code` for single-key bindings (layout-independent, SPEC §9.10). */
  code: string | null;
  /** What the action would do, for tooltips (SPEC §9.7 "tooltips spell out each action"). */
  semantics: string;
};

export const TRADING_ACTIONS: readonly TradingAction[] = [
  {
    id: "trade.buy_market",
    title: "Buy market",
    hotkey: "B",
    code: "KeyB",
    semantics: "Buys the selected quantity at market",
  },
  {
    id: "trade.sell_market",
    title: "Sell market",
    hotkey: "S",
    code: "KeyS",
    semantics: "Sells the selected quantity at market",
  },
  {
    id: "trade.join_bid",
    title: "Join bid",
    hotkey: "J",
    code: "KeyJ",
    semantics: "Buy limit at the current best bid",
  },
  {
    id: "trade.join_ask",
    title: "Join ask",
    hotkey: "A",
    code: "KeyA",
    semantics: "Sell limit at the current best ask",
  },
  {
    id: "trade.reverse",
    title: "Reverse & Cxl",
    hotkey: "R",
    code: "KeyR",
    semantics: "Cancels the symbol's working orders, then reverses the confirmed position at market",
  },
  {
    id: "trade.exit_cancel",
    title: "Exit & Cxl (symbol)",
    hotkey: "X",
    code: "KeyX",
    semantics: "Cancels working orders AND closes the symbol's position at market",
  },
  {
    id: "trade.cancel_all",
    title: "Cancel all (account)",
    hotkey: "C",
    code: "KeyC",
    semantics: "Cancels every working order on the account; positions untouched",
  },
  {
    id: "trade.flatten",
    title: "Flatten (account)",
    hotkey: "F",
    code: "KeyF",
    semantics: "Cancels all orders and closes every position on the account",
  },
  {
    id: "trade.flatten_all",
    title: "Flatten ALL accounts",
    hotkey: "Ctrl+Shift+F",
    code: null,
    semantics: "Flattens every connected account; asks first when 'Ask before Exit All' is on (Settings)",
  },
];

/** The single-key trading hotkeys (B S J A R F C X). */
export const TRADING_HOTKEY_CODES: ReadonlyMap<string, TradingAction> = new Map(
  TRADING_ACTIONS.filter((a) => a.code !== null).map((a) => [a.code ?? "", a]),
);

/** Tooltip for a disabled trading control: what it would do, then why it is disabled. */
export function disabledTooltip(
  semantics: string,
  hotkey?: string,
  reason: string = TRADING_DISABLED_REASON,
): string {
  return `${semantics}${hotkey ? ` (${hotkey})` : ""} — ${reason}`;
}

/** Minimal key-event shape so the matcher is testable without a DOM. */
export type KeyLike = {
  code: string;
  repeat: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
};

/**
 * Which trading hotkey a keydown maps to, or null. SPEC §9.10: single letters only fire when a
 * chart, DOM or pad has focus (`inTradingScope`) and never in text inputs (`inTextInput`);
 * repeats are ignored (one press = one action).
 */
export function matchTradingHotkey(
  e: KeyLike,
  inTradingScope: boolean,
  inTextInput: boolean,
): TradingAction | null {
  if (inTextInput) return null;
  if (e.ctrlKey && e.shiftKey && !e.altKey && !e.metaKey && e.code === "KeyF") {
    return TRADING_ACTIONS.find((a) => a.id === "trade.flatten_all") ?? null;
  }
  if (!inTradingScope || e.repeat || e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return null;
  return TRADING_HOTKEY_CODES.get(e.code) ?? null;
}
export * from "./orderSurface.ts";
